import { computed, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import { IUserStatusVM, MobileContextApi } from '@fleet-pro/mobile-webview-api';

import { isAxiosCancellationError } from '../utils/is-cancellation-error';
import { RequestState } from '../utils/request-states';
import { RequestManager } from './request-manager';

@injectable()
export class UserStatusDataStore extends RequestManager {
    @observable status: IUserStatusVM | undefined;

    readonly getUserStatusKey = 'getUserStatusKey';

    constructor(
        @inject(MobileContextApi) private readonly mobileContextApi: MobileContextApi
    ) {
        super();
        makeObservable(this);
    }

    cancelFetchUserStatus = () => {
        this.cancelRequest(this.getUserStatusKey);
    };

    @computed get userStatusRequestState() {
        return this.getRequestState(this.getUserStatusKey);
    }

    @computed get isOnboardingComplete() {
        if (!this.status) {
            return false;
        }

        return (
            (this.status.isUserRegistered &&
                this.status.tenantStatusVm?.isTenantRegistered &&
                this.status.tenantStatusVm?.isDataValidationCompleted &&
                this.status.tenantStatusVm?.isOnboardingCompleted) ??
            false
        );
    }

    fetchUserStatus = async () => {
        this.cancelRequest(this.getUserStatusKey);
        this.resetAbortController(this.getUserStatusKey);

        this.setRequestState(this.getUserStatusKey, RequestState.Loading);

        try {
            const response = await this.mobileContextApi.client.getUserStatus({
                signal: this.getAbortController(this.getUserStatusKey)?.signal,
            });

            runInAction(() => {
                this.status = response.data;
            });

            this.setRequestState(this.getUserStatusKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.getUserStatusKey, RequestState.Idle);
            } else {
                this.setRequestState(this.getUserStatusKey, RequestState.Error);
            }

            throw error;
        }
    };
}
